import React, { useEffect } from "react";
import { Button, Stack, Typography, useTheme } from "@mui/material";
import { useForm } from "react-hook-form";
import { useSnackbar } from "notistack";

import { FormProvider, RHFTextField, LocationSelector } from "@app/components";
import { RequestStatus, Strings } from "@app/constants";
import { useDialog, useResponsive } from "@app/hooks";

import { InvestorFormProps } from "../../types";
import { LoadingButton } from "@mui/lab";
import { useDispatch, useSelector } from "react-redux";
import {
  beAnInvestor,
  resetBeAnInvestorState,
} from "@app/modules/beAnInvestor/slices";
import { selectBeAnInvestorState } from "@app/modules/beAnInvestor/selectors";
import { InvestorSuccessDialog } from "@app/components/modals/investor-success-dialog";

const InvestorForm = ({ onFormValueChange, onClose }: InvestorFormProps) => {
  const isMobile = useResponsive("down", "md");
  const theme = useTheme();
  const dispatch = useDispatch();
  const { status, message, alreadyExists } = useSelector(
    selectBeAnInvestorState,
  );
  const investorSuccess = useDialog();
  const { enqueueSnackbar } = useSnackbar();

  const isLoading = status === RequestStatus.RUNNING;

  useEffect(() => {
    if (status === RequestStatus.SUCCESS) {
      if (alreadyExists) {
        enqueueSnackbar(message || "User already exists", {
          variant: "success",
        });
      } else {
        investorSuccess.show();
      }

      methods.reset();
    }

    if (status === RequestStatus.ERROR) {
      enqueueSnackbar(message || "Something went wrong", {
        variant: "error",
      });
    }
  }, [status]);

  const methods = useForm({
    defaultValues: {
      phone: "",
      email: "",
      firstName: "",
      lastName: "",
      state: "",
      city: "",
      zipCode: "",
    },
    mode: "onSubmit",
  });

  const handleBeAnInvestor = (data: any) => {
    console.log("Investor Form JSON:", JSON.stringify(data, null, 2));
    dispatch(beAnInvestor(data));
    onFormValueChange?.(data)
  };

  const FormField = ({
    label,
    name,
    placeholder,
    rules,
  }: {
    label: string;
    name: string;
    placeholder?: string;
    rules?: any;
  }) => (
    <Stack sx={{ flex: 1 }}>
      <Typography
        sx={{
          color: theme.palette.common.black,
          fontSize: isMobile ? 12 : 13,
          fontWeight: 500,
          mb: 0.5,
        }}
      >
        {label}
      </Typography>

      <RHFTextField
        name={name}
        placeholder={placeholder}
        fullWidth
        rules={rules}
        sx={{
          "& .MuiOutlinedInput-root": {
            backgroundColor: theme.palette.grey[200],
            borderRadius: 1.5,
            "& fieldset": { border: "none" },
          },
        }}
      />
    </Stack>
  );

  return (
    <Stack sx={{ width: "100%", alignSelf: "center" }}>
      <FormProvider methods={methods}>
        <Stack spacing={2} mt={2}>
          <Stack direction={isMobile ? "column" : "row"} spacing={2}>
            <FormField
              label="First Name"
              name="firstName"
              placeholder="First Name"
              rules={{
                required: "First name is required",
                minLength: {
                  value: 2,
                  message: "Minimum 2 characters required",
                },
              }}
            />

            <FormField
              label="Last Name"
              name="lastName"
              placeholder="Last Name"
              rules={{
                required: "Last name is required",
                minLength: {
                  value: 2,
                  message: "Minimum 2 characters required",
                },
              }}
            />
          </Stack>

          <Stack direction={isMobile ? "column" : "row"} spacing={2}>
            <FormField
              label={Strings.authPopup.phoneNumber}
              name="phone"
              placeholder="Phone Number*"
              rules={{
                required: "Phone number is required",
                pattern: {
                  value: /^[6-9]\d{9}$/,
                  message: "Enter valid 10 digit phone number",
                },
              }}
            />

            <FormField
              label={Strings.authPopup.email}
              name="email"
              placeholder="Email"
              rules={{
                required: "Email is required",
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: "Enter valid email address",
                },
              }}
            />
          </Stack>

          <LocationSelector />
        </Stack>

        <Stack
          direction={isMobile ? "column" : "row"}
          spacing={2}
          justifyContent="flex-end"
          sx={{ pt: 3 }}
        >
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{
              px: isMobile ? 2 : 6,
              py: 1.5,
              width: isMobile ? "100%" : "auto",
            }}
          >
            {Strings.button.cancel}
          </Button>

          <LoadingButton
            variant="contained"
            onClick={methods.handleSubmit(handleBeAnInvestor)}
            sx={{
              px: isMobile ? 2 : 6,
              py: 1.5,
              width: isMobile ? "100%" : "auto",
              fontWeight: 600,
            }}
            loading={isLoading}
          >
            {Strings.button.continue}
          </LoadingButton>
        </Stack>
      </FormProvider>
      <InvestorSuccessDialog
        open={investorSuccess.visible}
        onClose={() => {
          investorSuccess.hide();
          onClose();
          dispatch(resetBeAnInvestorState());
        }}
      />
    </Stack>
  );
};

export { InvestorForm };
